import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Download,
  ListFilter,
  Lock,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Trash2,
  Upload,
  Wand2,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const SESSION_KEY = 'pierino-pattern-lock-session';

type IntentSource = 'shipped' | 'overridden' | 'custom';

interface PatternExample {
  input: string;
  expect: { action: string; clientName?: string; searchTerm?: string; startTime?: string; date?: string };
}

interface PatternIntent {
  id: string;
  action: string;
  priority: number;
  confidence: number;
  entityType?: string;
  requestedField?: string;
  template: string;
  defaults?: { date?: string };
  skipIfTokens?: string[];
  note?: string;
  examples: PatternExample[];
  source?: IntentSource;
  enabled?: boolean;
}

interface Overlay {
  version: number;
  lock?: { sessionId: string; userId: string; email: string; until: string };
  disabledIntentIds: string[];
  intentOverrides: Record<string, PatternIntent>;
  extraIntents: PatternIntent[];
  tokenOverrides: Record<string, string>;
  extraTokens: Record<string, string>;
  disabledSynonymFrom: string[];
  extraSynonyms: Array<{ from: string; to: string }>;
  synonymOrder?: string[];
  time?: { roundToMinutes: number; inferAfternoonFrom: number; inferAfternoonTo: number };
  audit: Array<{ at: string; userId: string; email: string; kind: string; detail?: string }>;
}

interface CatalogPayload {
  catalog: {
    tokens: Record<string, string>;
    synonyms: Array<{ from: string; to: string }>;
    time: { roundToMinutes: number; inferAfternoonFrom: number; inferAfternoonTo: number };
  };
  intents: PatternIntent[];
  overlay: Overlay;
  lock?: Overlay['lock'];
  shippedTokens: Record<string, string>;
  shippedSynonyms: Array<{ from: string; to: string }>;
  actions: string[];
  tokenNames: string[];
}

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function withToken(init?: RequestInit): RequestInit {
  const token = localStorage.getItem('accessToken');
  return {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      'X-Pattern-Lock-Session': getSessionId(),
      ...(init?.headers || {}),
    },
  };
}

function emptyIntent(): PatternIntent {
  return {
    id: 'custom.',
    action: 'create_appointment',
    priority: 160,
    confidence: 0.9,
    entityType: 'appointment',
    template: '',
    examples: [{ input: '', expect: { action: 'create_appointment' } }],
  };
}

export function MessagePatternsSettingsSection() {
  const [data, setData] = useState<CatalogPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lockedByOther, setLockedByOther] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PatternIntent>(emptyIntent());
  const [isNew, setIsNew] = useState(false);

  const [testMessage, setTestMessage] = useState('');
  const [testNow, setTestNow] = useState('');
  const [testResult, setTestResult] = useState<any>(null);
  const [synonymFrom, setSynonymFrom] = useState('');

  const [extraTokenName, setExtraTokenName] = useState('');
  const [extraTokenValue, setExtraTokenValue] = useState('');
  const [extraSynFrom, setExtraSynFrom] = useState('');
  const [extraSynTo, setExtraSynTo] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState<any>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const canEdit = !lockedByOther;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/settings/message-patterns', withToken());
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      setData(json.data);
    } catch (e) {
      toast.error((e as Error).message || 'Errore caricamento parsing');
    } finally {
      setLoading(false);
    }
  }, []);

  const takeLock = useCallback(async () => {
    const res = await fetch('/api/settings/message-patterns/lock', withToken({
      method: 'POST',
      body: JSON.stringify({ sessionId: getSessionId() }),
    }));
    const json = await res.json();
    if (res.status === 423) {
      setLockedByOther(json.lock?.email || json.message || 'un altro admin');
      return;
    }
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    setLockedByOther(null);
  }, []);

  useEffect(() => {
    void (async () => {
      await load();
      await takeLock();
    })();
    const beat = window.setInterval(() => {
      void fetch('/api/settings/message-patterns/lock/heartbeat', withToken({
        method: 'POST',
        body: JSON.stringify({ sessionId: getSessionId() }),
      })).then(async (r) => {
        if (r.status === 423) {
          const j = await r.json();
          setLockedByOther(j.lock?.email || 'un altro admin');
        }
      });
    }, 30000);
    const unlock = () => {
      void fetch('/api/settings/message-patterns/lock', withToken({
        method: 'DELETE',
        body: JSON.stringify({ sessionId: getSessionId() }),
        keepalive: true,
      }));
    };
    window.addEventListener('pagehide', unlock);
    return () => {
      window.clearInterval(beat);
      window.removeEventListener('pagehide', unlock);
      unlock();
    };
  }, [load, takeLock]);

  const intents = data?.intents || [];
  const selected = useMemo(
    () => intents.find((i) => i.id === selectedId) || null,
    [intents, selectedId]
  );

  useEffect(() => {
    if (selected && !isNew) {
      setDraft({
        ...selected,
        examples: selected.examples?.length ? selected.examples : [{ input: '', expect: { action: selected.action } }],
        skipIfTokens: selected.skipIfTokens || [],
      });
    }
  }, [selected, isNew]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return intents;
    return intents.filter(
      (i) =>
        i.id.toLowerCase().includes(q) ||
        i.action.toLowerCase().includes(q) ||
        i.template.toLowerCase().includes(q)
    );
  }, [intents, filter]);

  const grouped = useMemo(() => {
    const map = new Map<string, PatternIntent[]>();
    for (const i of filtered) {
      const list = map.get(i.action) || [];
      list.push(i);
      map.set(i.action, list);
    }
    return [...map.entries()];
  }, [filtered]);

  async function saveIntent() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload: PatternIntent = {
        ...draft,
        examples: (draft.examples || []).filter((e) => e.input.trim()),
        skipIfTokens: (draft.skipIfTokens || []).filter(Boolean),
      };
      if (!payload.examples.length) {
        toast.error('Serve almeno un example con frase di input');
        return;
      }
      payload.examples = payload.examples.map((e) => ({
        ...e,
        expect: { ...e.expect, action: payload.action },
      }));
      const res = await fetch('/api/settings/message-patterns/intents', withToken({
        method: 'PUT',
        body: JSON.stringify({ intent: payload }),
      }));
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      if (json.data?.warning) toast.warning(json.data.warning);
      toast.success('Intent salvato');
      setIsNew(false);
      setSelectedId(payload.id);
      await load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(intent: PatternIntent, enabled: boolean) {
    if (!canEdit) return;
    const url = enabled
      ? `/api/settings/message-patterns/intents/${encodeURIComponent(intent.id)}/enable`
      : `/api/settings/message-patterns/intents/${encodeURIComponent(intent.id)}`;
    const res = await fetch(url, withToken({ method: enabled ? 'POST' : 'DELETE' }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    await load();
  }

  async function restoreIntent(id: string) {
    if (!canEdit) return;
    const res = await fetch(`/api/settings/message-patterns/intents/${encodeURIComponent(id)}/restore`, withToken({ method: 'POST' }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    toast.success('Intent ripristinato al default');
    await load();
  }

  async function runTest() {
    const res = await fetch('/api/settings/message-patterns/test', withToken({
      method: 'POST',
      body: JSON.stringify({ message: testMessage, now: testNow || undefined }),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    setTestResult(json.data);
    setSynonymFrom('');
  }

  async function addPlaygroundSynonym() {
    if (!canEdit) return;
    const dict = testResult?.dictionary;
    const toWord = dict?.canonical || dict?.word;
    if (!synonymFrom.trim() || !toWord) {
      toast.error('Scrivi il sinonimo da aggiungere');
      return;
    }
    const res = await fetch('/api/settings/message-patterns/synonyms/add', withToken({
      method: 'POST',
      body: JSON.stringify({ fromWord: synonymFrom.trim(), toWord }),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    toast.success(`Aggiunto: ${synonymFrom.trim()} → ${toWord} (attivo subito, senza rebuild)`);
    setSynonymFrom('');
    await load();
    await runTest();
  }

  async function suggestFromPhrase() {
    const res = await fetch('/api/settings/message-patterns/suggest-template', withToken({
      method: 'POST',
      body: JSON.stringify({ message: testMessage || draft.examples?.[0]?.input }),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    setDraft((d) => ({ ...d, template: json.data.template }));
    toast.success('Template precompilato: rivedilo prima di salvare');
  }

  async function saveTokens() {
    if (!data || !canEdit) return;
    const res = await fetch('/api/settings/message-patterns/tokens', withToken({
      method: 'PUT',
      body: JSON.stringify({
        tokenOverrides: data.overlay.tokenOverrides,
        extraTokens: data.overlay.extraTokens,
      }),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    toast.success('Token salvati');
    await load();
  }

  async function saveSynonyms() {
    if (!data || !canEdit) return;
    const res = await fetch('/api/settings/message-patterns/synonyms', withToken({
      method: 'PUT',
      body: JSON.stringify({
        disabledSynonymFrom: data.overlay.disabledSynonymFrom,
        extraSynonyms: data.overlay.extraSynonyms,
        synonymOrder: data.overlay.synonymOrder,
      }),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    toast.success('Sinonimi salvati');
    await load();
  }

  async function saveTime() {
    if (!data || !canEdit) return;
    const res = await fetch('/api/settings/message-patterns/time', withToken({
      method: 'PUT',
      body: JSON.stringify(data.catalog.time),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    toast.success('Regole orario salvate');
    await load();
  }

  async function resetAll() {
    if (!canEdit) return;
    const res = await fetch('/api/settings/message-patterns/reset', withToken({
      method: 'POST',
      body: JSON.stringify({ confirm: resetConfirm }),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    toast.success('Overlay azzerato');
    setResetConfirm('');
    await load();
  }

  async function previewImport() {
    try {
      const overlay = JSON.parse(importText);
      const res = await fetch('/api/settings/message-patterns/import', withToken({
        method: 'POST',
        body: JSON.stringify({ overlay, confirm: false }),
      }));
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      setImportPreview(json.data.preview);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function confirmImport() {
    const overlay = JSON.parse(importText);
    const res = await fetch('/api/settings/message-patterns/import', withToken({
      method: 'POST',
      body: JSON.stringify({ overlay, confirm: true }),
    }));
    const json = await res.json();
    if (!json.success) {
      toast.error(json.message);
      return;
    }
    toast.success('Overlay importato');
    setImportPreview(null);
    setImportText('');
    await load();
  }

  function insertToken(name: string) {
    setDraft((d) => ({ ...d, template: `${d.template}{${name}}` }));
  }

  if (loading && !data) {
    return <div className="text-sm text-gray-500">Caricamento catalogo parsing…</div>;
  }

  return (
    <div className="space-y-6">
      {lockedByOther && (
        <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <Lock className="h-4 w-4" />
          In modifica da {lockedByOther}. Puoi consultare e usare il playground; attendi che chiuda il tab (o 2 minuti dopo un crash).
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Playground</CardTitle>
          <CardDescription>
            Il matching avviene dopo minuscole e sinonimi. wouldExecute = WhatsApp/AI Pro esegue il tool (soglia 0.3).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={testMessage}
            onChange={(e) => setTestMessage(e.target.value)}
            placeholder="Mi prendi un appuntamento con Mario Rossi alle 10 domani?"
            rows={2}
          />
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <Label>Simula data/ora (opzionale)</Label>
              <Input type="datetime-local" value={testNow} onChange={(e) => setTestNow(e.target.value)} />
            </div>
            <Button onClick={() => void runTest()}>
              <Search className="mr-2 h-4 w-4" />
              Prova
            </Button>
            <Button variant="outline" onClick={() => void suggestFromPhrase()} disabled={!canEdit}>
              <Wand2 className="mr-2 h-4 w-4" />
              Parti da questa frase
            </Button>
          </div>
          {testResult && (
            <div className="space-y-2 rounded-md border bg-gray-50 p-3 text-sm">
              <div>
                <span className="font-medium">Normalizzato:</span> {testResult.normalized}
              </div>
              <div>
                Vincente: <code>{testResult.intentId || '—'}</code> → {testResult.extracted?.action}{' '}
                {testResult.wouldExecute ? (
                  <Badge>wouldExecute</Badge>
                ) : (
                  <Badge variant="secondary">fallback AI</Badge>
                )}
              </div>
              <pre className="overflow-auto text-xs">{JSON.stringify(testResult.extracted, null, 2)}</pre>
              {testResult.simulated && (
                <div>
                  <span className="font-medium">Tool simulato:</span>{' '}
                  {testResult.simulated.tool}.{testResult.simulated.action}
                  <pre className="overflow-auto text-xs">{JSON.stringify(testResult.simulated.params, null, 2)}</pre>
                </div>
              )}
              {testResult.runnersUp?.length > 0 && (
                <div>
                  <span className="font-medium">Altri match:</span>{' '}
                  {testResult.runnersUp.map((r: any) => r.intentId).join(', ')}
                </div>
              )}
              {testResult.unmappedGroups && Object.keys(testResult.unmappedGroups).length > 0 && (
                <div className="flex items-center gap-1 text-amber-700">
                  <AlertTriangle className="h-4 w-4" />
                  Gruppi extra (non usati dai tool): {JSON.stringify(testResult.unmappedGroups)}
                </div>
              )}
              {testResult.dictionary?.singleWord && (
                <div className="space-y-2 rounded-md border border-dashed bg-white p-3">
                  <div>
                    <span className="font-medium">Dizionario:</span>{' '}
                    {testResult.dictionary.inDictionary ? (
                      <>
                        sì, forma canonica <code>{testResult.dictionary.canonical}</code>
                        {testResult.dictionary.alreadyMapped
                          ? ` (questa parola è già un sinonimo → ${testResult.dictionary.normalizedWord})`
                          : null}
                      </>
                    ) : (
                      <>questa parola non è ancora una forma canonica</>
                    )}
                  </div>
                  <p className="text-xs text-gray-600">
                    Su una parola sola il fallback AI è normale. Puoi aggiungere un sinonimo: viene
                    salvato nell&apos;overlay e usato subito da WhatsApp/AI Pro.
                  </p>
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="space-y-1">
                      <Label>Sinonimo da aggiungere</Label>
                      <Input
                        value={synonymFrom}
                        disabled={!canEdit}
                        placeholder="es. avvisami"
                        onChange={(e) => setSynonymFrom(e.target.value)}
                      />
                    </div>
                    <div className="pb-2 text-xs text-gray-600">
                      → normalizza a{' '}
                      <code>{testResult.dictionary.canonical || testResult.dictionary.word}</code>
                    </div>
                    <Button
                      type="button"
                      disabled={!canEdit || !synonymFrom.trim()}
                      onClick={() => void addPlaygroundSynonym()}
                    >
                      Aggiungi
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Intent
              <Button
                size="sm"
                disabled={!canEdit}
                onClick={() => {
                  setIsNew(true);
                  setSelectedId(null);
                  setDraft(emptyIntent());
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Nuovo
              </Button>
            </CardTitle>
            <div className="relative">
              <ListFilter className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input className="pl-8" placeholder="Filtra id / action / template" value={filter} onChange={(e) => setFilter(e.target.value)} />
            </div>
          </CardHeader>
          <CardContent className="max-h-[640px] space-y-4 overflow-auto">
            {grouped.map(([action, list]) => (
              <div key={action}>
                <div className="mb-1 text-xs font-semibold uppercase text-gray-500">{action}</div>
                <div className="space-y-1">
                  {list.map((intent) => (
                    <button
                      key={intent.id}
                      type="button"
                      onClick={() => {
                        setIsNew(false);
                        setSelectedId(intent.id);
                      }}
                      className={`flex w-full items-center justify-between rounded border px-2 py-1.5 text-left text-sm ${
                        selectedId === intent.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white'
                      } ${intent.enabled === false ? 'opacity-50' : ''}`}
                    >
                      <span className="truncate font-mono text-xs">{intent.id}</span>
                      <span className="ml-2 flex items-center gap-1">
                        <Badge variant={intent.source === 'custom' ? 'default' : intent.source === 'overridden' ? 'secondary' : 'outline'}>
                          {intent.source}
                        </Badge>
                        <span className="text-xs text-gray-400">{intent.priority}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{isNew ? 'Nuovo intent custom.*' : draft.id || 'Editor'}</CardTitle>
            <CardDescription>Regex con token. Almeno un example deve matchare al salvataggio.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Id</Label>
                <Input
                  value={draft.id}
                  disabled={!isNew || !canEdit}
                  onChange={(e) => setDraft({ ...draft, id: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Action</Label>
                <Select
                  value={draft.action}
                  onValueChange={(v) => setDraft({ ...draft, action: v, examples: draft.examples.map((ex) => ({ ...ex, expect: { ...ex.expect, action: v } })) })}
                  disabled={!canEdit}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(data?.actions || []).map((a) => (
                      <SelectItem key={a} value={a}>
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Priority</Label>
                <Input
                  type="number"
                  value={draft.priority}
                  disabled={!canEdit}
                  onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label>Confidence</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={draft.confidence}
                  disabled={!canEdit}
                  onChange={(e) => setDraft({ ...draft, confidence: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1">
                <Label>entityType</Label>
                <Input
                  value={draft.entityType || ''}
                  disabled={!canEdit}
                  onChange={(e) => setDraft({ ...draft, entityType: e.target.value || undefined })}
                />
              </div>
              <div className="space-y-1">
                <Label>requestedField</Label>
                <Input
                  value={draft.requestedField || ''}
                  disabled={!canEdit}
                  onChange={(e) => setDraft({ ...draft, requestedField: e.target.value || undefined })}
                />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label>defaults.date</Label>
                <Input
                  value={draft.defaults?.date || ''}
                  disabled={!canEdit}
                  onChange={(e) => setDraft({ ...draft, defaults: e.target.value ? { date: e.target.value } : undefined })}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Template</Label>
              <div className="mb-1 flex flex-wrap gap-1">
                {(data?.tokenNames || []).map((t) => (
                  <Button key={t} type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" disabled={!canEdit} onClick={() => insertToken(t)}>
                    {`{${t}}`}
                  </Button>
                ))}
              </div>
              <Textarea
                className="font-mono text-xs"
                rows={4}
                value={draft.template}
                disabled={!canEdit}
                onChange={(e) => setDraft({ ...draft, template: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label>skipIfTokens (virgola)</Label>
              <Input
                value={(draft.skipIfTokens || []).join(',')}
                disabled={!canEdit}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    skipIfTokens: e.target.value.split(',').map((s) => s.trim()).filter(Boolean),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Examples</Label>
              {(draft.examples || []).map((ex, idx) => (
                <div key={idx} className="grid gap-2 rounded border p-2">
                  <Input
                    placeholder="Frase di input"
                    value={ex.input}
                    disabled={!canEdit}
                    onChange={(e) => {
                      const next = [...draft.examples];
                      next[idx] = { ...ex, input: e.target.value };
                      setDraft({ ...draft, examples: next });
                    }}
                  />
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canEdit}
                onClick={() =>
                  setDraft({
                    ...draft,
                    examples: [...(draft.examples || []), { input: '', expect: { action: draft.action } }],
                  })
                }
              >
                Aggiungi example
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void saveIntent()} disabled={!canEdit || saving}>
                <Save className="mr-2 h-4 w-4" />
                Salva
              </Button>
              {selected && selected.source !== 'custom' && (
                <>
                  <Button variant="outline" disabled={!canEdit} onClick={() => void toggleEnabled(selected, selected.enabled === false)}>
                    {selected.enabled === false ? 'Riabilita' : 'Disabilita'}
                  </Button>
                  {selected.source === 'overridden' && (
                    <Button variant="outline" disabled={!canEdit} onClick={() => void restoreIntent(selected.id)}>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Ripristina questo
                    </Button>
                  )}
                </>
              )}
              {selected?.source === 'custom' && (
                <Button
                  variant="destructive"
                  disabled={!canEdit}
                  onClick={() => void toggleEnabled(selected, false)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Elimina
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Token</CardTitle>
          <CardDescription>Override dei token shipped e token extra. I tipi pratica/attività si aggiornano al save overlay o al restart.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="max-h-56 overflow-auto rounded border">
            <table className="w-full text-xs">
              <tbody>
                {Object.entries(data?.catalog.tokens || {}).map(([name, value]) => (
                  <tr key={name} className="border-b">
                    <td className="w-40 px-2 py-1 font-mono">{name}</td>
                    <td className="px-2 py-1">
                      <Input
                        className="font-mono text-xs"
                        value={data?.overlay.tokenOverrides[name] ?? data?.overlay.extraTokens[name] ?? value}
                        disabled={!canEdit}
                        onChange={(e) => {
                          if (!data) return;
                          const shippedHas = name in data.shippedTokens;
                          if (shippedHas) {
                            setData({
                              ...data,
                              overlay: {
                                ...data.overlay,
                                tokenOverrides: { ...data.overlay.tokenOverrides, [name]: e.target.value },
                              },
                            });
                          } else {
                            setData({
                              ...data,
                              overlay: {
                                ...data.overlay,
                                extraTokens: { ...data.overlay.extraTokens, [name]: e.target.value },
                              },
                            });
                          }
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <Input placeholder="nome extra" value={extraTokenName} onChange={(e) => setExtraTokenName(e.target.value)} />
            <Input className="min-w-[240px] flex-1 font-mono" placeholder="regex" value={extraTokenValue} onChange={(e) => setExtraTokenValue(e.target.value)} />
            <Button
              variant="outline"
              disabled={!canEdit}
              onClick={() => {
                if (!data || !extraTokenName) return;
                setData({
                  ...data,
                  overlay: {
                    ...data.overlay,
                    extraTokens: { ...data.overlay.extraTokens, [extraTokenName]: extraTokenValue },
                  },
                  catalog: {
                    ...data.catalog,
                    tokens: { ...data.catalog.tokens, [extraTokenName]: extraTokenValue },
                  },
                });
                setExtraTokenName('');
                setExtraTokenValue('');
              }}
            >
              Aggiungi token
            </Button>
            <Button onClick={() => void saveTokens()} disabled={!canEdit}>
              Salva token
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sinonimi</CardTitle>
          <CardDescription>Ordine rilevante: i più specifici devono stare sopra. Disabilita o aggiungi extra, poi salva.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="max-h-56 space-y-1 overflow-auto">
            {(data?.catalog.synonyms || []).map((s) => {
              const disabled = data?.overlay.disabledSynonymFrom.includes(s.from);
              return (
                <label key={s.from} className="flex items-center gap-2 text-xs">
                  <Switch
                    checked={!disabled}
                    disabled={!canEdit}
                    onCheckedChange={(on) => {
                      if (!data) return;
                      const set = new Set(data.overlay.disabledSynonymFrom);
                      if (on) set.delete(s.from);
                      else set.add(s.from);
                      setData({
                        ...data,
                        overlay: { ...data.overlay, disabledSynonymFrom: [...set] },
                      });
                    }}
                  />
                  <span className={`font-mono ${disabled ? 'text-gray-400 line-through' : ''}`}>
                    {s.from} → {s.to}
                  </span>
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <Input placeholder="from regex" className="font-mono" value={extraSynFrom} onChange={(e) => setExtraSynFrom(e.target.value)} />
            <Input placeholder="to" value={extraSynTo} onChange={(e) => setExtraSynTo(e.target.value)} />
            <Button
              variant="outline"
              disabled={!canEdit}
              onClick={() => {
                if (!data || !extraSynFrom) return;
                setData({
                  ...data,
                  overlay: {
                    ...data.overlay,
                    extraSynonyms: [...data.overlay.extraSynonyms, { from: extraSynFrom, to: extraSynTo }],
                  },
                  catalog: {
                    ...data.catalog,
                    synonyms: [...data.catalog.synonyms, { from: extraSynFrom, to: extraSynTo }],
                  },
                });
                setExtraSynFrom('');
                setExtraSynTo('');
              }}
            >
              Aggiungi sinonimo
            </Button>
            <Button onClick={() => void saveSynonyms()} disabled={!canEdit}>
              Salva sinonimi
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <button type="button" className="flex w-full items-center justify-between text-left" onClick={() => setAdvancedOpen((v) => !v)}>
            <CardTitle>Avanzate — regole orario</CardTitle>
            <span className="text-sm text-gray-500">{advancedOpen ? 'nascondi' : 'mostra'}</span>
          </button>
        </CardHeader>
        {advancedOpen && data && (
          <CardContent className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label>roundToMinutes</Label>
              <Input
                type="number"
                value={data.catalog.time.roundToMinutes}
                disabled={!canEdit}
                onChange={(e) =>
                  setData({
                    ...data,
                    catalog: { ...data.catalog, time: { ...data.catalog.time, roundToMinutes: Number(e.target.value) } },
                  })
                }
              />
            </div>
            <div className="space-y-1">
              <Label>inferAfternoonFrom</Label>
              <Input
                type="number"
                value={data.catalog.time.inferAfternoonFrom}
                disabled={!canEdit}
                onChange={(e) =>
                  setData({
                    ...data,
                    catalog: { ...data.catalog, time: { ...data.catalog.time, inferAfternoonFrom: Number(e.target.value) } },
                  })
                }
              />
            </div>
            <div className="space-y-1">
              <Label>inferAfternoonTo</Label>
              <Input
                type="number"
                value={data.catalog.time.inferAfternoonTo}
                disabled={!canEdit}
                onChange={(e) =>
                  setData({
                    ...data,
                    catalog: { ...data.catalog, time: { ...data.catalog.time, inferAfternoonTo: Number(e.target.value) } },
                  })
                }
              />
            </div>
            <Button className="sm:col-span-3" onClick={() => void saveTime()} disabled={!canEdit}>
              Salva regole orario
            </Button>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Export / import / reset</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                const res = await fetch('/api/settings/message-patterns/export', withToken());
                const json = await res.json();
                const blob = new Blob([JSON.stringify(json.data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'message-patterns-overlay.json';
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Download className="mr-2 h-4 w-4" />
              Export overlay
            </Button>
            <Button variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload className="mr-2 h-4 w-4" />
              Carica JSON
            </Button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setImportText(await file.text());
              }}
            />
          </div>
          <Textarea
            className="font-mono text-xs"
            rows={6}
            placeholder="Incolla overlay JSON"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={!canEdit || !importText} onClick={() => void previewImport()}>
              Preview import
            </Button>
            {importPreview && (
              <>
                <span className="text-sm text-gray-600">{JSON.stringify(importPreview)}</span>
                <Button disabled={!canEdit} onClick={() => void confirmImport()}>
                  Conferma replace
                </Button>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-2 border-t pt-4">
            <div className="space-y-1">
              <Label>Reset totale (scrivi RESET)</Label>
              <Input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} />
            </div>
            <Button variant="destructive" disabled={!canEdit || resetConfirm !== 'RESET'} onClick={() => void resetAll()}>
              Azzera overlay
            </Button>
            <Button variant="ghost" onClick={() => void load()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Ricarica
            </Button>
          </div>
          {data?.overlay.audit?.length ? (
            <div className="space-y-1 text-xs text-gray-600">
              <div className="font-medium">Ultimi salvataggi</div>
              {data.overlay.audit.slice(0, 20).map((a, i) => (
                <div key={`${a.at}-${i}`}>
                  {a.at} — {a.email} — {a.kind}
                  {a.detail ? ` (${a.detail})` : ''}
                </div>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
